<template>
  <form class="advance-form" @submit.prevent="submit">
    <app-form-group :label="$t('finance.amount')" required>
      <app-fin-number
        v-model="amount"
        placeholder="0"
        inputmode="numeric"
        :error="$v.amount.$error"
        @input="invalidatePreview"
      />
      <template #error>
        <div v-if="$v.amount.$dirty && !$v.amount.required">{{ $t('common.required') }}</div>
        <div v-else-if="$v.amount.$dirty && !$v.amount.minValue">{{ $t('advances.minimumAmount') }}</div>
      </template>
    </app-form-group>
    <app-form-group :label="$t('common.note')">
      <app-textarea v-model="notes" :placeholder="$t('advances.notePlaceholder')" />
    </app-form-group>
    <app-button ref="calculate" @click.native="calculate">{{ $t('common.calculate') }}</app-button>

    <template v-if="preview">
      <div class="advance-form__divider"></div>
      <div class="advance-form__eligibility" :class="{ 'advance-form__eligibility--error': !preview.eligible }">
        <strong>{{ preview.eligible ? $t('common.available') : $t('common.notAvailable') }}</strong> &mdash; {{ preview.reason.name }}
      </div>
      <cabinet-rows :rows="previewRows" />
      <button class="advance-form__disclosure" type="button" @click="detailsExpanded = !detailsExpanded">
        {{ detailsExpanded ? $t('common.hideCalculation') : $t('common.calculation') }}
      </button>
      <cabinet-rows v-if="detailsExpanded" class="advance-form__details" :rows="detailRows" />
      <app-button
        v-if="preview.eligible && !$v.$invalid"
        ref="submit"
        class="btn--stretch advance-form__submit"
        theme="request"
        type="submit"
        :disabled="preview.duplicate_for_month"
      >
        {{ $t('common.submitRequest') }}
      </app-button>
    </template>
  </form>
</template>

<script>
import { getPersonalCabinetAdvancePreview, postPersonalCabinetAdvance } from '@/http'
import { minValue, required } from 'vuelidate/lib/validators'
import CabinetRows from './CabinetRows'
import { formatMoney, formatMonth, formatPercent } from './cabinetDisplay'

export default {
  name: 'AdvanceRequestForm',
  components: { CabinetRows },
  data() {
    return {
      amount: null,
      notes: null,
      preview: null,
      detailsExpanded: false,
      validationAttempted: false,
    }
  },
  validations: {
    amount: { required, minValue: minValue(30000) },
  },
  computed: {
    previewRows() {
      return [
        { label: this.$t('finance.requestedAmount'), value: formatMoney(this.preview.amount, this.preview.currency) },
        { label: this.$t('finance.maximumAvailable'), value: formatMoney(this.preview.max_advance_amount, this.preview.currency) },
        { label: this.$t('finance.payrollMonth'), value: formatMonth(this.preview.payroll_month) },
        { label: this.$t('finance.expectedSalaryAfter'), value: formatMoney(this.preview.accrued_salary_after, this.preview.currency) },
      ]
    },
    detailRows() {
      return [
        { label: this.$t('finance.grossSalary'), value: formatMoney(this.preview.gross_salary, this.preview.currency) },
        { label: this.$t('finance.netSalaryBeforeDebt'), value: formatMoney(this.preview.net_salary_before_debt, this.preview.currency) },
        { label: this.$t('finance.salaryPercentAfter'), value: formatPercent(this.preview.accrued_salary_percent_after) },
      ]
    },
  },
  methods: {
    invalidatePreview() {
      this.preview = null
    },
    calculate() {
      this.$v.amount.$touch()
      const amount = Number(this.amount)
      if (this.$v.amount.$invalid) return
      this.$refs.calculate.preload = true
      getPersonalCabinetAdvancePreview({ amount })
        .then(response => {
          this.preview = response.data
          this.detailsExpanded = false
        })
        .finally(() => {
          this.$refs.calculate.preload = false
        })
    },
    submit() {
      this.validationAttempted = true
      this.$v.$touch()
      if (this.$v.$invalid) {
        this.$notify({
          type: 'info',
          title: this.$t('common.formInvalid'),
        })
        return
      }
      if (!this.preview?.eligible || this.preview.duplicate_for_month) return
      this.$refs.submit.preload = true
      const data = { amount: Number(this.amount) }
      if (this.notes?.trim()) data.employee_notes = this.notes.trim()
      postPersonalCabinetAdvance(data)
        .then(() => {
          this.$emit('created')
        })
        .finally(() => {
          if (this.$refs.submit) this.$refs.submit.preload = false
        })
    },
  },
}
</script>

<style lang="sass" scoped>
.advance-form__divider
  margin: 18px 0
  border-top: 1px solid #ededed

.advance-form__eligibility,
.advance-form__warning
  padding: 13px 15px
  border-radius: 9px

.advance-form__eligibility
  background-color: #eff8e9
  color: #4f8d12

.advance-form__eligibility--error,
.advance-form__warning
  background-color: #fff0f0
  color: #c63b3b

.advance-form__warning
  margin-top: 8px

.advance-form__disclosure
  margin-top: 14px
  color: #4f8d12
  font-size: 14px
  font-weight: 600

.advance-form__details
  margin-top: 10px
  padding: 10px 14px
  border-radius: 10px
  background-color: #f8f8f8

.advance-form__submit
  margin-top: 14px
</style>
